import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TbsButtonModule } from '@ngx-terabase/ui/button';
import { TbsDatePickerModule } from '@ngx-terabase/ui/date-picker';
import { TbsFormModule } from '@ngx-terabase/ui/form';
import { TbsSelectModule } from '@ngx-terabase/ui/select';

import {
  NotificationType,
  TbsNotificationModule,
  TbsNotificationService,
} from '@ngx-terabase/ui/notification';
import { map, Subject, takeUntil } from 'rxjs';
import { MissionService } from 'src/app/services/mission.service';
import { ConstructionProject, Pagination } from 'src/app/types';

import 'moment-timezone';
import moment, { Moment } from 'moment';
import { TbsInputModule } from '@ngx-terabase/ui/input';
import { environment } from 'src/environments/environment';

export type MissionCreationResult = {
  missionId: string;
  missionPath: string;
  constructionProject: ConstructionProject;
  date: Moment;
};

@Component({
  selector: 'app-new-mission',
  imports: [
    CommonModule,
    TbsFormModule,
    TbsDatePickerModule,
    TbsSelectModule,
    TbsButtonModule,
    TbsNotificationModule,
    TbsInputModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  templateUrl: './new-mission.component.html',
  styleUrl: './new-mission.component.scss',
  standalone: true,
})
export class NewMissionComponent implements OnDestroy {
  @Output() public missionCreate = new EventEmitter<MissionCreationResult>();

  private http = inject(HttpClient);
  private formBuilder = inject(FormBuilder);
  private missionService = inject(MissionService);
  private notification = inject(TbsNotificationService);

  public form = this.formBuilder.group({
    project: [null as ConstructionProject | null, Validators.required],
    date: [new Date(), Validators.required],
    time: ['12:00 PM', Validators.required],
  });

  public loading = false;

  public projects$ = this.http
    .get<{ data: ConstructionProject[]; pagination: Pagination }>(
      `${environment.apiUrl}/construction_projects/`,
      { params: { page_size: 1000 } }
    )
    .pipe(map((response) => response.data));

  public timeOptions = this.generateTimeOptions();

  private destroy$ = new Subject<void>();

  onSubmit() {
    const form = this.form.getRawValue();

    const momentDate = moment(`${form.date!.toDateString()} ${form.time!}`).tz(
      form.project!.timezone,
      true
    );

    this.loading = true;
    this.missionService
      .createMission(form.project!._id, momentDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.notification.snackBar({
            title: 'Mission created',
            type: NotificationType.Success,
            duration: 5000,
            message: 'Successfully created mission',
          });
          this.missionCreate.emit({
            constructionProject: form.project!,
            date: momentDate,
            missionPath: response.storage_path,
            missionId: response._id
          });
        },
        error: () => {
          this.loading = false;
          this.notification.snackBar({
            title: 'Error',
            type: NotificationType.Error,
            duration: 5000,
            message: 'Failed to create a mission',
          });
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private generateTimeOptions() {
    const options: string[] = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const hour12 = h % 12 === 0 ? 12 : h % 12;
        const minute = m.toString().padStart(2, '0');
        const ampm = h < 12 ? 'AM' : 'PM';
        const formatted = `${hour12}:${minute} ${ampm}`;
        options.push(formatted);
      }
    }
    return options;
  }
}
